/**
 * WPS Process Grid Components
 * 
 * Reusable input components for WPS (Welding Procedure Specification) process grids.
 * Provides standardized input fields with error handling, validation, and responsive design.
 * 
 * WPS_DETAIL STRUCTURE:
 * wps_detail is an array of objects representing each column in the grid: 
 * The array has 3 processes, each array posistion has one process object given below
 * [
 *   {
 *     // Single process fields
 *     fieldName: "value",
 *     process: "GTAW",
 *     thickness: "10mm",
 *     
 *     // Array fields for triple inputs (filler metal dependent)
 *     fillermetal: ["", "", ""],           // Controls which inputs are enabled
 *     amperage: ["", "", ""],              // Depends on fillermetal values
 *     voltage: ["", "", ""],               // Depends on fillermetal values
 *     
 *     // Array fields for amperage ranges
 *     amperageFrom: ["", "", ""],          // "From" values
 *     amperageTo: ["", "", ""],            // "To" values
 *     
 *     // Master data fields
 *     welddesc: ["", "", ""],              // Auto-populated from selections
 *     weldweight: ["", "", ""],            // Auto-populated from selections
 *   },
 *   // ... more column objects
 * ]
 * 
 * COMPONENT USAGE:
 * - WPSProcessGridInput: Single input fields (Groove/Tubesheet forms)
 * - WPS_Process_Grid_InputProcess: Read-only process type fields (GTAW)
 * - WPS_Process_Grid_Input_3Filermetal: Triple filler metal inputs
 * - WPS_Process_Grid_Input_2_Amperes: Amperage range inputs (from/to)
 * - WPS_Process_Grid_Input_3: Triple inputs with filler dependencies
 * - WPSProcessGridSelect: Single dropdown selections
 * - WPS_Process_Grid_Select_3: Triple dropdown selections
 * - WPSProcessGridInput2: Dual input fields with headers
 * - WPS_Grid_Input_2: Simple dual inputs
 * - WPS_Process_Grid_Select_master_data_TTS: Master data dropdowns
 */

// If FillerMetalSize is empty → BatchNumber is disabled
// If FillerMetalSize has value → BatchNumber is enabled

import { Tooltip_detail as Tooltip } from "../../utility/common/helpers/Tooltip";
import Img from "../../../icons/ErrorIcon.svg";

import { BiSelectMultiple } from "react-icons/bi";
import {
  DoubleInput, DoubleInputRow, TripleInput, TripleInputWithdDpendency,
  TripleSelectWithDependecny, getInputWidth, getRowClassName, getCellClassName, GridHeader, INPUT_WIDTHS, CSS_CLASSES, safeArrayAccess,
  SingleProccessInput, Process_Grid_Input_2
} from "../../utility/common/TransactionComponents/Common_WPS_WPQ_PQR_Process_Input_Fields"

// ============================================================================
// EXPORTED GRID COMPONENTS
// Main components used in WPS forms
// ============================================================================

// Single input field component - Used in Groove and Tubesheet forms for basic text inputs
export const WPSProcessGridInput = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  readOnly,
  handleChange,
  disabled,
  maxLength,
  validatorFn,
  setError,
  isTubesheet = false,
}) => {
  return (
    <tr className={getRowClassName(status)}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => (
        <SingleProccessInput
          key={item}
          item={item}
          name={name}
          type={type}
          maxLength={maxLength}
          readOnly={readOnly}
          disabled={disabled}
          status={status}
          validatorFn={validatorFn}
          setError={setError}
          id={id}
          handleChange={handleChange}
          isTubesheet={isTubesheet}
        />
      ))}
    </tr>
  );
};



// Alias for backward compatibility
export const WPSProcessGridInputTubesheet = WPSProcessGridInput;

// Read-only process type input - Used to display "GTAW" process type
export const WPS_Process_Grid_InputProcess_tubesheet = ({
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  disabled,
  maxLength,
  isTubesheet = false,
  value = "GTAW"
}) => {
  return (
    <tr className={CSS_CLASSES.ROW_BASE}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
      />

      {wps_detail.map((item, id) => (
        <td className={CSS_CLASSES.CELL_BASE} >
          <input
            style={{ width: getInputWidth(isTubesheet) }}
            type={type}
            name={name}
            value={value}
            readOnly
            disabled={disabled}
            maxLength={maxLength}
          />
        </td>
      ))}
    </tr>
  );
};


// Triple filler metal input - Used for filler metal specifications with 3 columns
export const WPS_Process_Grid_Input_3Filermetal = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  readOnly,
  handleChangeFillermetal,
  maxLength,
  disabled,
  validatorFn,
  setError
}) => {
  return (
    <tr className={getRowClassName(status)}>
      <th className={CSS_CLASSES.HEADER}>
        <div>
          {ColumnHeader}
          <span style={{ textAlign: "right" }}>{Columndimension}</span>
          {status && (
            <Tooltip content={message}>
              <img src={Img} alt="Error" className="ms-1" />
            </Tooltip>
          )}
        </div>
      </th>
      {wps_detail.map((item, id) => (
        <td className={getCellClassName(message)} >
          <TripleInput
            item={item}
            name={name}
            type={type}
            maxLength={maxLength}
            readOnly={readOnly}
            disabled={disabled}
            status={status}
            validatorFn={validatorFn}
            setError={setError}
            id={id}
            onChange={handleChangeFillermetal}
          />
        </td>
      ))}
    </tr>
  );
};

// Amperage range input - Used for current ranges (from/to) with filler dependencies
export const WPS_Process_Grid_Input_2_Amperes = ({
  status,
  status1,
  message,
  message1,
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  name1,
  readOnly,
  handleChange,
  maxLength,
  onInput,
  fieldfiller,
  disabled
}) => {
  const hasError = status || status1;
  const errorMessage = message || message1;

  // Initialize arrays for each item to prevent runtime errors
  const initializeArrays = (item) => {
    item[fieldfiller] = safeArrayAccess(item[fieldfiller]);
    item[name] = safeArrayAccess(item[name]);
    item[name1] = safeArrayAccess(item[name1]);
  };

  return (
    <tr className={getRowClassName(hasError)}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={hasError}
        message={errorMessage}
      />
      {wps_detail.map((item, id) => {
        initializeArrays(item);
        return (
          <td className={getCellClassName(errorMessage)} >
            <DoubleInputRow
              item={item}
              id={id}
              name={name}
              name1={name1}
              fieldfiller={fieldfiller}
              type={type}
              maxLength={maxLength}
              readOnly={readOnly}
              disabled={disabled}
              status={hasError}
              onInput={onInput}
              handleChange={handleChange}
            />
          </td>
        );
      })}
    </tr>
  );
};

// Triple input with filler dependency - Used for inputs that depend on filler metal selection
export const WPS_Process_Grid_Input_3 = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  readOnly,
  disabled,
  handleChange,
  maxLength,
  setError,
  validatorFn,
  fieldfiller,
}) => {

  return (

    <tr className={getRowClassName(status)}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => (
        <td className={getCellClassName(message)} >
          <TripleInputWithdDpendency
            item={item}
            id={id}
            name={name}
            fieldfiller={fieldfiller}
            type={type}
            maxLength={maxLength}
            readOnly={readOnly}
            disabled={disabled}
            status={status}
            validatorFn={validatorFn}
            setError={setError}
            handleChange={handleChange}
          />
        </td>
      ))}
    </tr>
  );
};

// Single dropdown selection - Used for single-value dropdowns in forms
export const WPSProcessGridSelect = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  name,
  readOnly,
  array,
  handleChange,
  disabled,
}) => {
  return (
    <tr className={getRowClassName(status)}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => (
        <>
          {console.log("filler", item, id)}
          <td className={getCellClassName(status)} >
            <select
              style={{
                width: INPUT_WIDTHS.STANDARD,
                height: "30px",
                borderRadius: "3px",
              }}
              className={status ? CSS_CLASSES.INPUT_ERROR : ""}
              readOnly={readOnly}
              disabled={disabled}
              name={name}
              value={item[name]}
              onChange={(e) => handleChange(e, id)}
            >
              {array?.map((e, idx) => (
                <option value={e.value}>
                  {e.name}
                </option>
              ))}
            </select>
          </td></>
      ))}
    </tr>
  );
};

// Dual input with headers - Used for inputs with main heading and dual sub-inputs
//Used in Sheilding Gas
export const WPSProcessGridInput2 = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  readOnly,
  disabled,
  mainheading,
  onInput1,
  handleChange,
  maxLength,
  onInput,
  ColumnHeader1,
  name1,
  isTubesheet = false,
  customWidth
}) => {
  const inputWidth = isTubesheet ? "250px" : "275px";
  return (
    <Process_Grid_Input_2
      status={status}
      message={message}
      ColumnHeader={ColumnHeader}
      Columndimension={Columndimension}
      type={type}
      name={name}
      readOnly={readOnly}
      mainheading={mainheading}
      handleChangedetailsarray={handleChange}
      maxLength={maxLength}
      ColumnHeader1={ColumnHeader1}
      name1={name1}
      width={inputWidth}
      wps_detail={wps_detail}
      onInput={onInput}
      onInput1={onInput1}
      width1={customWidth}
    />
  );
};
// Alias for backward compatibility
export const WPSProcessGridInput2Tubesheet = WPSProcessGridInput2;

// Triple dropdown selection - Used for 3-column dropdown selections with filler dependencies
export const WPS_Process_Grid_Select_3 = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  name,
  array,
  handleChange,

  fieldfiller,
  disabled
}) => {
  return (
    <tr className={getRowClassName(status)}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => (
        <td className={getCellClassName(message)} >
          <TripleSelectWithDependecny
            item={item}
            id={id}
            name={name}
            fieldfiller={fieldfiller}
            array={array}
            disabled={disabled}
            status={status}
            handleChange={handleChange}
          />
        </td>
      ))}
    </tr>
  );
};


// Master data dropdown selection - Used for TTS and standard Item data selections
export const WPS_Process_Grid_Select_master_data_TTS = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  data,
  name,
  setError,
  valueField,
  labelField,
  setdetailObj,
  detailObj,
  fieldfiller,
  disabled,
  isTTS = false,
  setIsEdited,
  showPopUp,
  headerObj
}) => {
  const handleChange = (id, index, name, detailObj, setdetailObj, e) => {
    setIsEdited(true)
    let selectedItem = data.find(b => b[valueField] === e.target.value);
    let onChangeVal = [...detailObj];
    onChangeVal[id][name][index] = e.target.value;
    onChangeVal[id]["WPSD_WP_WeldDesc"][index] = selectedItem?.wld_description || "";
    onChangeVal[id]["weldweight"][index] = selectedItem?.wld_weight || "";
    setdetailObj(onChangeVal)
    setError((prevState) => ({
      ...prevState,
      [name]: { status: false, message: "" },
    }));
  };

  const selectWidth = isTTS ? "250px" : "85px";
  const selectCount = isTTS ? [0, 1] : [0, 1, 2];

  return (
    <tr className={status ? "border WpsInput-md error" : "border WpsInput-md"}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => {
        if (!isTTS) {
          if (!Array.isArray(item[fieldfiller])) {
            item[fieldfiller] = ["", "", ""];
          }
          if (!Array.isArray(item[name])) {
            item[name] = ["", "", ""];
          }
        }
        else {
          if (!Array.isArray(item[name])) {
            item[name] = ["", ""];
          }
        }

        return (
          <td className={message ? "border WPSSelect error" : "border WPSSelect"}>
            <div>
              {selectCount.map((index) => {
                const isDisabled = !isTTS && (item[fieldfiller]?.[index] === "" || item[fieldfiller]?.[index] === undefined);

                if (!isTTS && isDisabled) {
                  item[name][index] = "";
                }

                return (
                  <div key={index} style={{ position: "relative", display: "inline-block" }}>
                    <select
                      style={{
                        width: selectWidth,
                        height: "32px",
                        borderRadius: "4px",
                        marginRight: "10px",
                        marginLeft: index === 0 ? "10px" : "0",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        backgroundImage: "none",
                        paddingRight: "25px",
                      }}
                      className={status ? "error-input" : ""}
                      name={`${name}${index + 1}`}
                      value={isDisabled ? "" : (item[name] && item[name][index])}
                      disabled={isDisabled || disabled}
                      onChange={(e) => handleChange(id, index, name, detailObj, setdetailObj, e)}
                    >
                      <option value="">Select</option>
                      {data?.filter(e => e[valueField] !== "NA").map((e) => (
                        <option key={e[valueField]} value={e[valueField]}>
                          {e[labelField]}
                        </option>
                      ))}
                    </select>
                    {showPopUp && headerObj?.WPSH_Project_No && (
                      <BiSelectMultiple
                        size={16}
                        onClick={() => {
                          showPopUp(
                            "ItemActive",
                            "Single",
                            "wld_itemcode",
                            item[name] && item[name][index],
                            id,
                            index
                          );
                        }}
                        color={"gray"}
                        style={{
                          position: "absolute",
                          right: "15px",
                          top: "8px",
                          cursor: "pointer",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
// New component with popup functionality
export const WPS_Process_Grid_Select_master_data_with_popup = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  data,
  name,
  setError,
  valueField,
  labelField,
  setdetailObj,
  detailObj,
  fieldfiller,
  disabled,
  isTTS = false,
  setIsEdited,
  showPopUp,
  headerObj
}) => {
  

  const selectWidth = isTTS ? "250px" : "85px";
  const selectCount = isTTS ? [0, 1] : [0, 1, 2];

  return (
    <tr className={status ? "border WpsInput-md error" : "border WpsInput-md"}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => {
        if (!isTTS) {
          if (!Array.isArray(item[fieldfiller])) {
            item[fieldfiller] = ["", "", ""];
          }
          if (!Array.isArray(item[name])) {
            item[name] = ["", "", ""];
          }
        }
        else {
          if (!Array.isArray(item[name])) {
            item[name] = ["", ""];
          }
        }

        return (
          <td className={message ? "border WPSSelect error" : "border WPSSelect"}>
            <div>
              {selectCount.map((index) => {
                const isDisabled = !isTTS && (item[fieldfiller]?.[index] === "" || item[fieldfiller]?.[index] === undefined);

                if (!isTTS && isDisabled) {
                  item[name][index] = "";
                }

                return (
                  <div key={index} style={{ position: "relative", display: "inline-block" }}>
                    <input
                      style={{
                        width: selectWidth,
                        height: "32px",
                        borderRadius: "4px",
                        marginRight: "10px",
                        marginLeft: index === 0 ? "10px" : "0",
                      }}
                      className={status ? "error-input" : ""}
                      name={`${name}${index + 1}`}
                      type="text"
                      value={isDisabled ? "" : (item[name] && item[name][index])}
                      
                       readOnly
                  
                    />
                    {!isTTS && !isDisabled && !disabled && showPopUp && headerObj?.WPSH_Project_No && (
                      <BiSelectMultiple
                        size={16}
                        onClick={() => {
                          showPopUp(
                            "ItemActive",
                            "Single",
                            "wld_itemcode",
                            item[name] && item[name][index],
                            id,
                            index
                          );
                        }}
                        color={"gray"}
                        style={{
                          position: "absolute",
                          right: "15px",
                          top: "8px",
                          cursor: "pointer",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </td>
        );
      })}
    </tr>
  );
};

// Alias for backward compatibility
export const WPS_Process_Grid_Select_master_data = WPS_Process_Grid_Select_master_data_TTS;

// Simple dual input - Used for basic 2-column input fields
export const WPS_Grid_Input_2 = ({
  status,
  message,
  wps_detail,
  ColumnHeader,
  Columndimension,
  type,
  name,
  readOnly,
  handleChange,
  maxLength,
  obj,
  setfunction,
  onInput,
  isTubesheet = false,
  fieldfiller,
}) => {
  return (
    <tr className={getRowClassName(status)}>
      <GridHeader
        ColumnHeader={ColumnHeader}
        Columndimension={Columndimension}
        status={status}
        message={message}
      />
      {wps_detail.map((item, id) => (
        <td className={getCellClassName(status)} >
          <DoubleInput
            item={item}
            id={id}
            name={name}
            type={type}
            maxLength={maxLength}
            readOnly={readOnly}
            status={status}
            onInput={onInput}
            handleChange={handleChange}
            obj={obj}
            setfunction={setfunction}
            isTubesheet={isTubesheet}
            fieldfiller={fieldfiller}
          />
        </td>
      ))}
    </tr>
  );
};
// Alias for backward compatibility
export const WPS_Grid_Input_2_Tubesheet = WPS_Grid_Input_2;
